import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, ArrowRight } from "lucide-react";
import { fetchBilanci } from "../api";

const SEM = {
  verde: { emoji: "🟢", label: "Solida", rank: 0, color: "#2e7d32" },
  giallo: { emoji: "🟡", label: "Attenzione", rank: 1, color: "#b7791f" },
  rosso: { emoji: "🔴", label: "Critica", rank: 2, color: "#d32f2f" },
};

const Bilancio60Index = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sector, setSector] = useState("Tutti");
  const [sort, setSort] = useState("nome");

  useEffect(() => {
    fetchBilanci()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const sectors = useMemo(
    () => ["Tutti", ...Array.from(new Set(items.map((i) => i.settore)))],
    [items]
  );

  const list = useMemo(() => {
    let l = items.filter((i) => sector === "Tutti" || i.settore === sector);
    l = [...l].sort((a, b) => {
      if (sort === "semaforo") {
        return (SEM[a.semaforo_salute]?.rank ?? 9) - (SEM[b.semaforo_salute]?.rank ?? 9);
      }
      return (a.societa || "").localeCompare(b.societa || "");
    });
    return l;
  }, [items, sector, sort]);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 fade-in">
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-sq-orange">Home</Link>
        <span>/</span>
        <span className="text-sq-navy font-medium">Bilancio in 60 secondi</span>
      </div>

      <h1 className="font-serif-display text-4xl text-sq-navy mb-2">
        Bilancio in 60 secondi — Euronext Growth Milan
      </h1>
      <p className="text-gray-600 max-w-2xl">
        Analisi rapida e indipendente delle PMI quotate su EGM.
      </p>
      <div className="w-16 h-1 bg-sq-orange mt-3 mb-8" />

      {/* Transparency ranking promo card (distinct blue border) */}
      <Link
        to="/trasparenza-egm"
        className="group flex items-center gap-4 border-2 border-[#1f4680] rounded-lg p-5 mb-8 bg-white hover:bg-[#f4f7fc] transition-colors"
      >
        <span className="text-3xl">🔍</span>
        <div className="flex-1">
          <h2 className="font-serif-display text-xl text-sq-navy">Classifica Trasparenza EGM</h2>
          <p className="text-sm text-gray-500">Chi comunica meglio con i propri azionisti? Scopri il ranking di trasparenza.</p>
        </div>
        <span className="text-[#1f4680] flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all">
          Vai alla classifica <ArrowRight size={16} />
        </span>
      </Link>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Settore</label>
          <select
            value={sector}
            onChange={(e) => setSector(e.target.value)}
            className="border border-sq rounded-sm px-3 py-2.5 text-sm outline-none bg-white min-w-[220px] focus:border-sq-orange"
          >
            {sectors.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Ordina per</label>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="border border-sq rounded-sm px-3 py-2.5 text-sm outline-none bg-white min-w-[180px] focus:border-sq-orange"
          >
            <option value="nome">Nome (A-Z)</option>
            <option value="semaforo">Semaforo (migliori prima)</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-400">Caricamento...</p>
      ) : list.length === 0 ? (
        <p className="text-gray-500">Nessuna società disponibile in questa selezione.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((b) => {
            if (b.stato === "in_lavorazione") {
              return (
                <div
                  key={b.id}
                  className="bg-gray-50 border border-dashed border-gray-300 rounded-md p-5 flex flex-col opacity-90"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="bg-gray-400 text-white text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded">EGM</span>
                      <h3 className="font-serif-display text-xl text-gray-500 mt-2 leading-snug">{b.societa}</h3>
                      <span className="text-xs text-gray-400 font-semibold">{b.ticker}</span>
                    </div>
                    <span className="text-[10px] uppercase tracking-wide font-bold text-gray-500 bg-gray-200 px-2 py-1 rounded-full">In arrivo</span>
                  </div>
                  <p className="text-sm text-gray-400 mt-3 flex-1">{b.settore}</p>
                  <div className="mt-4 pt-3 border-t border-gray-200 text-xs text-gray-400 italic">
                    Scheda in preparazione
                  </div>
                </div>
              );
            }
            const sem = SEM[b.semaforo_salute] || SEM.giallo;
            const up = (b.ricavi?.variazione_percentuale ?? 0) >= 0;
            return (
              <Link
                key={b.id}
                to={`/bilancio-60-secondi/${b.ticker}`}
                className="group bg-white border border-sq rounded-md p-5 hover:shadow-lg transition-shadow duration-300 flex flex-col"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <span className="bg-sq-orange text-white text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded">EGM</span>
                    <h3 className="font-serif-display text-xl text-sq-navy mt-2 leading-snug">{b.societa}</h3>
                    <span className="text-xs text-gray-400 font-semibold">{b.ticker}</span>
                  </div>
                  <span className="text-2xl leading-none" title={sem.label}>{sem.emoji}</span>
                </div>

                <p className="text-sm text-gray-500 mt-3 flex-1">{b.settore}</p>

                <div className="flex items-center justify-between mt-4 pt-3 border-t border-sq">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-500">Ricavi</span>
                    <span
                      className={`text-sm font-bold flex items-center gap-0.5 ${up ? "text-emerald-600" : "text-red-600"}`}
                    >
                      {up ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                      {up ? "↑" : "↓"} {Math.abs(b.ricavi?.variazione_percentuale ?? 0)}%
                    </span>
                  </div>
                  <span className="text-sq-orange flex items-center gap-1 text-sm font-medium group-hover:gap-2 transition-all">
                    Scheda <ArrowRight size={15} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Bilancio60Index;
